import apiClient from "services/ApiClient";
import type { ResponseModel } from "services/ApiClient/responseModel";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";

import type { CustomForm, CustomFormResponse, CustomFormResponseSummary } from "./types";
import type {
  CreateCustomFormRequest,
  CustomFormFieldPayload,
  SubmitCustomFormResponseRequest,
  UpdateCustomFormRequest,
} from "./models/payloads";

const customForms = API_ROUTES.customForms;
const taskCustomFormResponse = API_ROUTES.taskCustomFormResponse;

export async function fetchCustomForms(query: string, signal?: AbortSignal): Promise<ResponseModel<CustomForm[]>> {
  return apiClient
    .performRequest<ResponseModel<CustomForm[]>>(`${customForms.base}${query}`, undefined, signal)
    .then((response) => response.payload);
}

export async function getCustomForm(id: number | string): Promise<CustomForm> {
  return apiClient.get<CustomForm>(generatePath(customForms.show, { id: String(id) }));
}

export async function createCustomForm(payload: CreateCustomFormRequest): Promise<CustomForm> {
  return apiClient.post<CustomForm>(customForms.base, payload);
}

export async function updateCustomForm(id: number | string, payload: UpdateCustomFormRequest): Promise<CustomForm> {
  return apiClient.put<CustomForm>(generatePath(customForms.show, { id: String(id) }), payload);
}

// Soft-archives + cascades server-side; treat as gone from the FE.
export async function deleteCustomForm(id: number | string): Promise<CustomForm> {
  return apiClient.delete<CustomForm>(generatePath(customForms.show, { id: String(id) }));
}

export interface CustomFormDeleteImpact {
  taskTemplatesCount: number;
  pendingTasksCount: number;
}

export async function fetchCustomFormDeleteImpact(id: number | string): Promise<CustomFormDeleteImpact> {
  return apiClient.get<CustomFormDeleteImpact>(generatePath(customForms.impact, { id: String(id) }));
}

// Field mutations return the updated form so callers can refresh the detail cache in one shot.
export async function createCustomFormField(
  formId: number | string,
  payload: CustomFormFieldPayload,
): Promise<CustomForm> {
  const path = generatePath(customForms.fields, { id: String(formId) });
  return apiClient.post<CustomForm>(path, { customFormField: payload });
}

export async function updateCustomFormField(
  formId: number | string,
  fieldId: number,
  payload: CustomFormFieldPayload,
): Promise<CustomForm> {
  const path = generatePath(customForms.field, { id: String(formId), fieldId: String(fieldId) });
  return apiClient.put<CustomForm>(path, { customFormField: payload });
}

export async function deleteCustomFormField(formId: number | string, fieldId: number): Promise<CustomForm> {
  const path = generatePath(customForms.field, { id: String(formId), fieldId: String(fieldId) });
  return apiClient.delete<CustomForm>(path);
}

export async function reorderCustomFormFields(formId: number | string, fieldIds: number[]): Promise<CustomForm> {
  const path = generatePath(customForms.fieldsOrder, { id: String(formId) });
  return apiClient.put<CustomForm>(path, { fieldIds });
}

export async function fetchCustomFormResponses(
  formId: number | string,
  query: string,
  signal?: AbortSignal,
): Promise<ResponseModel<CustomFormResponse[]>> {
  const path = generatePath(customForms.responses, { id: String(formId) });
  return apiClient
    .performRequest<ResponseModel<CustomFormResponse[]>>(`${path}${query}`, undefined, signal)
    .then((response) => response.payload);
}

export async function getCustomFormResponseForTask(taskId: number | string): Promise<CustomFormResponseSummary | null> {
  const path = generatePath(taskCustomFormResponse, { taskId: String(taskId) });
  return apiClient.get<CustomFormResponseSummary | null>(path);
}

export async function submitCustomFormResponse(
  taskId: number | string,
  payload: SubmitCustomFormResponseRequest,
): Promise<CustomFormResponse> {
  const path = generatePath(taskCustomFormResponse, { taskId: String(taskId) });
  return apiClient.post<CustomFormResponse>(path, payload);
}

export function csvExportUrl(formId: number | string, columns?: string[]): string {
  // generatePath strips the trailing `.csv` from the pattern, so build it manually.
  const base = `/api/custom-forms/${encodeURIComponent(String(formId))}/responses.csv`;
  if (!columns || columns.length === 0) return base;
  return `${base}?columns=${encodeURIComponent(columns.join(","))}`;
}

export async function getCustomFormResponse(id: number | string): Promise<CustomFormResponse> {
  return apiClient.get<CustomFormResponse>(generatePath(API_ROUTES.customFormResponses.show, { id: String(id) }));
}

export async function deleteCustomFormResponse(id: number | string): Promise<void> {
  await apiClient.delete(generatePath(API_ROUTES.customFormResponses.show, { id: String(id) }));
}
